import { parseISO } from 'date-fns';
import { CM_SESSION_IDLE_TIMEOUT } from 'features/creditMonitoring/CreditMonitoringConstants';
import objectHash from 'object-hash';

import { pageDataService } from './PageDataService';

const OnboardingSkipToDashboard = 'onboarding_skipToDashboard';
const DownloadDefenderModalSkipToDashboard = 'downloadDefenderModal_skipToDashboard';
const LastConsentRequestTimestampKey = 'lastConsentRequestCreatedDateTime';
const AddPhoneRecoCardDismiss = 'dashboard_addPhoneRecoCardDismiss';
const CreditReportTipDismiss = 'creditReportTip';
const ActiveStateLastUpdatedKey = 'cmSessionTimeStamp';

export interface ILocalStorageService {
  /**
   * Returns the current value associated with the given key, or null if the given key does not exist in the list associated with the object.
   */
  getItem(key: string): string | null;

  /**
   * Sets the value of the pair identified by key to value, creating a new key/value pair if none existed for key previously.
   */
  setItem(key: string, value: string): void;

  /**
   * Removes the item by key
   */
  removeItem(key: string): void;

  /**
   * Gets OnboardingSkipToDashboard state
   */
  getOnboardingSkipToDashboard(): boolean;

  /**
   * Gets Download defender OnboardingSkipToDashboard state
   */
  getDownloadDefenderModalSkipToDashboard(): boolean;

  /**
   * Sets OnboardingSkipToDashboard state to true
   */
  markOnboardingSkipToDashboard(): void;

  /**
   * Sets DownloadDefenderModalSkipToDashboard state to true
   */
  markDownloadDefenderModalSkipToDashboard(): void;

  /**
   * Returns the current value associated with the given key for current user, or null if the given key does not exist in the list associated with the object.
   */
  getUserScopedItem(key: string): string | null;

  /**
   * Sets the value of the pair identified by key to value for current user, creating a new key/value pair if none existed for key previously.
   */
  setUserScopedItem(key: string, value: string): void;

  /**
   * Removes the item by key for current user
   */
  removeUserScopedItem(key: string): void;

  /**
   * Sets last requested consent request date for given user
   */
  setConsentRequestLastUpdatedTime(requestedUserId: string, date: Date): void;

  /**
   * Gets last requested consent request date for given user
   */
  getConsentRequestLastUpdatedTime(requestedUserId: string): Date | null;

  /**
   * Gets AddPhoneRecoCardDismiss state
   */
  getAddPhoneRecoCardDismiss(): boolean;

  /**
   * Sets AddPhoneRecoCardDismiss state to true
   */
  markAddPhoneRecoCardDismiss(): void;

  /**
   * Sets latest month credit report tip dismissal
   */
  setLatestMonthCreditReportTipDismissal(reportDate: Date, requestedUserId: string, tipType: string): void;

  /**
   * Gets latest month credit report tip dismissal
   */
  getLatestMonthCreditReportTipDismissal(reportDate: Date, requestedUserId: string, tipType: string): boolean;

  /**
   * Checks if the session is active
   */
  isSessionActive(): boolean;

  /**
   * Removes session data
   */
  removeSessionData(): void;

  /**
   * Updates the session timestamp
   */
  reactivateSession(): void;
}

/**
 *
 * Service for reading and writing to local storage
 */
class LocalStorageService implements ILocalStorageService {
  // NOTE: eslint false positives due to typescring interface usage, see: https://github.com/typescript-eslint/typescript-eslint/issues/1103
  /* eslint-disable class-methods-use-this */
  public getItem(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      console.warn('localStorage is not available in your browser. Please update your browser settings to turn on localStorage.');
      return null;
    }
  }

  public setItem(key: string, value: string): void {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      console.warn('localStorage is not available in your browser. Please update your browser settings to turn on localStorage.');
    }
  }

  public removeItem(key: string): void {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      console.warn('localStorage is not available in your browser. Please update your browser settings to turn on localStorage.');
    }
  }

  public getOnboardingSkipToDashboard(): boolean {
    const key = this.createUserKey(OnboardingSkipToDashboard);

    try {
      return window.localStorage.getItem(key) === 'true';
    } catch (e) {
      // In case local storage is not supported, return true so user doesn't get stuck to onboarding flow
      return true;
    }
  }

  public getDownloadDefenderModalSkipToDashboard(): boolean {
    const key = this.createUserKey(DownloadDefenderModalSkipToDashboard);

    try {
      return window.localStorage.getItem(key) === 'true';
    } catch (e) {
      // In case local storage is not supported, return true so user doesn't get stuck to onboarding flow
      return true;
    }
  }

  public markOnboardingSkipToDashboard() {
    this.setUserScopedItem(OnboardingSkipToDashboard, 'true');
  }

  public markDownloadDefenderModalSkipToDashboard() {
    this.setUserScopedItem(DownloadDefenderModalSkipToDashboard, 'true');
  }

  public getUserScopedItem(key: string): string | null {
    return this.getItem(this.createUserKey(key));
  }

  public setUserScopedItem(key: string, value: string): void {
    this.setItem(this.createUserKey(key), value);
  }

  public removeUserScopedItem(key: string): void {
    this.removeItem(this.createUserKey(key));
  }

  public setConsentRequestLastUpdatedTime(requestedUserId: string, date: Date) {
    const hashedMemberId = objectHash({ value: requestedUserId });
    this.setUserScopedItem(`${LastConsentRequestTimestampKey}_${hashedMemberId}`, date.toISOString());
  }

  public getConsentRequestLastUpdatedTime(requestedUserId: string): Date | null {
    const hashedMemberId = objectHash({ value: requestedUserId });
    const dateStr = this.getUserScopedItem(`${LastConsentRequestTimestampKey}_${hashedMemberId}`);
    return dateStr ? parseISO(dateStr) : null;
  }

  private createUserKey(key: string) {
    const hash = objectHash({ value: pageDataService.getUserData().csid });
    return key + hash;
  }

  public getAddPhoneRecoCardDismiss(): boolean {
    const key = this.createUserKey(AddPhoneRecoCardDismiss);

    try {
      return window.localStorage.getItem(key) === 'true';
    } catch (e) {
      // In case local storage is not supported
      return true;
    }
  }

  public markAddPhoneRecoCardDismiss() {
    this.setUserScopedItem(AddPhoneRecoCardDismiss, 'true');
  }

  public setLatestMonthCreditReportTipDismissal(reportDate: Date, requestedUserId: string, tipType: string) {
    const hashedMemberId = objectHash({ value: requestedUserId });
    this.setUserScopedItem(`${CreditReportTipDismiss}_${tipType}_${hashedMemberId}`, reportDate.toISOString());
  }

  public getLatestMonthCreditReportTipDismissal(reportDate: Date, requestedUserId: string, tipType: string) {
    const hashedMemberId = objectHash({ value: requestedUserId });
    const key = `${CreditReportTipDismiss}_${tipType}_${hashedMemberId}`;

    try {
      return this.getUserScopedItem(key) === reportDate.toISOString();
    } catch (e) {
      // In case local storage is not supported
      return true;
    }
  }

  public removeSessionData() {
    this.removeItem(ActiveStateLastUpdatedKey);
  }

  public reactivateSession() {
    const now = Date.now();
    this.setUserScopedItem(ActiveStateLastUpdatedKey, `${now}`);
  }

  // Is session active or not
  // To preserve session activity across page reloads
  // If you lock and reload, still locked etc
  public isSessionActive(): boolean {
    const timestamp = this.getUserScopedItem(ActiveStateLastUpdatedKey);

    if (!timestamp) {
      return false;
    }

    const lastActiveTime = parseFloat(timestamp);
    const now = Date.now();
    const timeDiff = now - lastActiveTime;

    if (timeDiff <= CM_SESSION_IDLE_TIMEOUT) {
      return true;
    }

    return false;
  }
}

export const localStorageService: ILocalStorageService = new LocalStorageService();
